import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { dspLabel } from '../format';
import { useReportWidget } from './useReportWidget';

export interface ConversionPlatform {
  /** Display name of the one platform the conversions belong to, when there is exactly one. */
  platform: string | null;
  /** Total conversions on that platform. Zero whenever `platform` is null. */
  conversions: number;
  /** True until the query settles. Callers render nothing while this is true. */
  loading: boolean;
}

const toNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Resolves the single platform a conversion figure can be attributed to, or null.
 *
 * Conversions cannot be joined across DSPs — each platform counts them its own
 * way — so a funnel spanning several platforms would be a number we can't stand
 * behind. Rather than trusting the filter bar to tell us (a campaign or
 * line-item selection narrows to one platform without `dsps` ever being set),
 * this asks the data: group conversions by `dsp` under the active filters and
 * see how many platforms actually come back with any.
 *
 * That makes "one platform" and "has conversions" the same question, which is
 * what the funnel needs to know — and it means the widget lights up on its own
 * once conversion ingestion lands, with no further UI change.
 */
/**
 * The decision itself, split out from the fetch so it can be tested directly:
 * exactly one platform with conversions resolves; none or several do not.
 */
export const resolveConversionPlatform = (rows: ReportRow[]): Omit<ConversionPlatform, 'loading'> => {
  const withConversions = rows.filter(r => toNumber(r.conversions) > 0);
  if (withConversions.length !== 1) return { platform: null, conversions: 0 };

  const [row] = withConversions;
  return { platform: dspLabel(row.dsp), conversions: toNumber(row.conversions) };
};

export const useConversionPlatform = (): ConversionPlatform => {
  const { rows, loading } = useReportWidget('conversionPlatform', {
    dimensions: ['dsp'],
    metrics: ['conversions'],
  });

  if (loading) return { platform: null, conversions: 0, loading: true };
  return { ...resolveConversionPlatform(rows), loading: false };
};
